import { useState, useEffect, useRef } from 'react';
import { FaCertificate, FaSearchPlus, FaTimes } from 'react-icons/fa';
import './Certificates.css';

import imgAmbientCert from '../certs/ambient cert.jpg';
import imgArduino1 from '../certs/arduino 1 cert.png';
import imgArduino2 from '../certs/arduino 2 cert.png';
import imgBantay from '../certs/Bantay Kalikasan Seminar cert.png';
import imgBuilding from '../certs/Building Intelligence cert.png';
import imgCareer from '../certs/Career Stories cert.png';
import imgCIDR from '../certs/CIDR Conference cert.png';
import imgRISCV from '../certs/Design of RISC-V Microprocesso cert.png';
import imgHIV from '../certs/hiv cert.png';
import imgCPElympics from '../certs/cpelympics cert.jpeg';

function Certificates() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [selectedCert, setSelectedCert] = useState(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const certificates = [
    {
      title: 'Arduino Workshop Part 1',
      organizer: 'FAITH CpE Society',
      desc: 'Hands-on workshop covering Arduino fundamentals, sensor interfacing, and embedded systems programming.',
      category: 'Workshop',
      image: imgArduino1
    },
    {
      title: 'Arduino Workshop Part 2',
      organizer: 'FAITH CpE Society',
      desc: 'Advanced workshop covering motor control, complex sensor interfacing, and system integration.',
      category: 'Workshop',
      image: imgArduino2
    },
    {
      title: 'Building Intelligence: CPE meets Data Science',
      organizer: 'FAITH CpE Society',
      desc: 'Explored the intersection of Computer Engineering and Data Science with hands-on exercises and lectures.',
      category: 'Technical',
      image: imgBuilding
    },
    {
      title: 'Design of RISC-V Microprocessor',
      organizer: 'FAITH CpE Society',
      desc: 'Technical seminar on RISC-V architecture, microprocessor design principles, and open-source hardware development.',
      category: 'Technical',
      image: imgRISCV
    },
    {
      title: '3rd Annual CIDR Conference',
      organizer: 'FAITH Colleges Research Office',
      desc: 'Annual conference on Innovation and Design Research, featuring research presentations and academic discourse.',
      category: 'Conference',
      image: imgCIDR
    },
    {
      title: 'Ambient Light-Based Smart Lighting System',
      organizer: 'FAITH Colleges',
      desc: 'Research and prototype exhibition certificate for Ambient Light Smart Lighting System.',
      category: 'Research',
      image: imgAmbientCert
    },
    {
      title: 'Bantay Kalikasan Seminar',
      organizer: 'FAITH Colleges',
      desc: 'Environmental awareness seminar focusing on sustainability, conservation, and eco-friendly engineering practices.',
      category: 'Seminar',
      image: imgBantay
    },
    {
      title: 'HIV Awareness & Community Empowerment',
      organizer: 'FAITH Colleges',
      desc: 'Seminar on HIV awareness, prevention, and community empowerment strategies for health advocacy.',
      category: 'Seminar',
      image: imgHIV
    },
    {
      title: 'Career Stories & Industry Insights',
      organizer: 'FAITH Colleges',
      desc: 'Industry professionals shared career journeys, insights, and advice for engineering students.',
      category: 'Seminar',
      image: imgCareer
    },
    {
      title: 'CPElympics Participation',
      organizer: 'ICpEP.se - FAITH Chapter',
      desc: 'Active participation and engagement in the annual CPElympics engineering competitions and events.',
      category: 'Event',
      image: imgCPElympics
    }
  ];

  return (
    <section className="certificates-page">
      {/* Header */}
      <div
        className={`certificates-header ${visibleSections.has('header') ? 'visible' : ''}`}
        data-section="header"
        ref={(el) => (sectionRefs.current[0] = el)}
      >
        <div className="header-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="certificates-header-content">
          <h1 className="page-title">Certificates & Seminars</h1>
          <p className="page-subtitle">Seminars, webinars, workshops, and trainings I've attended and completed</p>
        </div>
      </div>

      {/* Gallery */}
      <div className="certificates-body">
        <div className="certificates-container">
          <div
            className={`cert-grid-section ${visibleSections.has('grid') ? 'visible' : ''}`}
            data-section="grid"
            ref={(el) => (sectionRefs.current[1] = el)}
          >
            <div className="cert-grid">
              {certificates.map((cert, index) => (
                <div
                  key={index}
                  className="cert-gallery-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => setSelectedCert(cert)}
                >
                  <div className="cert-image-wrapper">
                    {cert.image ? (
                      <img src={cert.image} alt={cert.title} />
                    ) : (
                      <div className="cert-placeholder"><FaCertificate /></div>
                    )}
                    <div className="cert-overlay">
                      <FaSearchPlus className="zoom-icon" />
                    </div>
                  </div>
                  <div className="cert-card-info">
                    <span className="cert-category">{cert.category}</span>
                    <h3 className="cert-title">{cert.title}</h3>
                    <p className="cert-organizer">{cert.organizer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedCert && (
        <div className="cert-modal-overlay" onClick={() => setSelectedCert(null)}>
          <div className="cert-modal" onClick={(e) => e.stopPropagation()}>
            <button className="cert-modal-close" onClick={() => setSelectedCert(null)}>
              <FaTimes />
            </button>
            <div className="cert-modal-visuals">
              {selectedCert.image ? (
                <img src={selectedCert.image} alt={selectedCert.title} className="cert-modal-image" />
              ) : (
                <div className="cert-modal-placeholder">
                  <FaCertificate />
                  <span>Certificate preview unavailable</span>
                </div>
              )}
            </div>
            <div className="cert-modal-details">
              <span className="cert-modal-category">{selectedCert.category}</span>
              <h2 className="cert-modal-title">{selectedCert.title}</h2>
              <h4 className="cert-modal-organizer">{selectedCert.organizer}</h4>
              <p className="cert-modal-desc">{selectedCert.desc}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Certificates;
